import DOMPurify, { type DOMPurify as Purifier } from 'dompurify';

/**
 * 受信メールの HTML から、動くもの・画面を偽るもの・外へ送るものを落とす。
 * 表示はスクリプトを許さないサンドボックスの iframe（mail-body.tsx）で行い、ここはその手前の守り。
 * CSP の無い環境（開発時など）でも、これだけで危険なものが残らないようにする。
 */
const FORBID_TAGS = [
  // 入力・送信（フィッシングの偽フォーム）
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'option',
  // 文書の設定・読み込み（meta refresh・base の差し替え・外部の CSS）
  'meta',
  'link',
  'base',
  // 別の文書・プラグイン
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'portal',
  // SVG・MathML（属性やアニメーションでの抜け道が多く、メールでは要らない）
  'svg',
  'math',
];

const FORBID_ATTR = ['formaction', 'action', 'ping', 'srcdoc'];

const configured = new WeakSet<Purifier>();

const configure = (purify: Purifier): Purifier => {
  if (configured.has(purify)) return purify;
  // リンクは新しいタブで開き、開いた先から元の画面を触らせない・参照元を送らない
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  configured.add(purify);
  return purify;
};

// フックが既定の DOMPurify（ほかの画面と共有）にかからないよう、メール用に別のインスタンスを作る
let mailPurify: Purifier | undefined;

/** purify はテストで jsdom の window に結び付けたものを渡す */
export const sanitizeMailHtml = (html: string, purify?: Purifier): string =>
  configure(purify ?? (mailPurify ??= DOMPurify(window))).sanitize(html, {
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    // 先頭の <style>（メールのレイアウト）を落とさない
    FORCE_BODY: true,
    // リンク等は http・https・mailto・tel とページ内（#）だけを通す。
    // 相対（/settings）や // で始まる URL は、メールでは意味がなくアプリの画面や別のサイトに飛ぶので落とす。
    // img の data: は DOMPurify が別に許す（画像として読み込む SVG ではスクリプトは動かない）
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#)/i,
  }) as string;
